import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useBranding } from "../lib/branding";
import { isPasswordValid, PASSWORD_MIN_LEN } from "../lib/passwordPolicy";
import PasswordStrengthChecklist from "../components/PasswordStrengthChecklist";

export default function ResetPassword() {
  const branding = useBranding();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("This reset link is missing its token. Request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!isPasswordValid(password)) {
      setErr(`Password must be at least ${PASSWORD_MIN_LEN} characters and include a letter, a number, and a symbol.`);
      return;
    }
    if (password !== confirm) { setErr("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    }
    setLoading(false);
  };

  return (
    <div className="auth-split" data-testid="reset-shell">
      <div className="auth-split-left">
        <div className="auth-split-logo">
          <img src={branding.logo_data_url || "/pragma-login-logo-v4.png"} alt="Pragma" />
        </div>
        <div className="auth-split-form-wrap">
          {done ? (
            <>
              <h1 className="auth-split-h1" data-testid="reset-done-heading">
                <CheckCircle2 size={24} style={{ display: "inline", verticalAlign: -3, color: "var(--pg-lime, #34a853)", marginRight: 8 }} />
                Password updated
              </h1>
              <p className="auth-split-sub">You'll be redirected to sign in…</p>
            </>
          ) : (
            <>
              <h1 className="auth-split-h1" data-testid="reset-heading">Set a new password</h1>
              <p className="auth-split-sub">Pick a strong password to secure your account.</p>
              <form onSubmit={submit} data-testid="reset-form">
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="auth-split-input" placeholder="New password"
                  data-testid="reset-password" required autoComplete="new-password" minLength={PASSWORD_MIN_LEN}
                />
                <PasswordStrengthChecklist password={password} testid="reset-password-rules" />
                <input
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="auth-split-input" placeholder="Confirm new password"
                  data-testid="reset-confirm" required autoComplete="new-password" minLength={PASSWORD_MIN_LEN}
                />
                {err && <div className="auth-split-error" data-testid="reset-error">{err}</div>}
                <div className="auth-split-actions">
                  <Link to="/login" className="auth-split-secondary">Back to sign in</Link>
                  <button type="submit" className="auth-split-submit" disabled={loading || !token} data-testid="reset-submit">
                    {loading ? "Updating…" : "Update password"}
                    <ArrowRight strokeWidth={2.2} />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      <div className="auth-split-right">
        <h2 className="auth-split-right-h1">
          {branding.heading_line_1}<br /><span className="accent">{branding.heading_line_2}</span>
        </h2>
      </div>
    </div>
  );
}
